import cn from 'classnames';
import React, { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useStyles } from './styles';

export type BreadcrumbItem = {
  label: string;
  url?: string;
  icon?: React.ReactNode;
};

type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
};

type LinkComponentProps = { to?: string; children: React.ReactNode };

const DEFAULT_SEPARATOR = '>';

const LinkComponent = ({ to, children }: LinkComponentProps) => {
  const styles = useStyles();
  const external = useMemo(() => {
    if (!to) return false;

    const url = URL.canParse(to) ? new URL(to) : window.location;
    return url?.origin !== window.location.origin;
  }, [ to ]);

  if (!to) return <>{children}</>;

  return external
    ? (
      <a className={styles.itemLink} data-testid="breadcrumbs__item-external-link" href={to}>
        {children}
      </a>
    )
    : (
      <Link className={styles.itemLink} data-testid="breadcrumbs__item-link" to={to} href={to}>
        {children}
      </Link>
    );
};

const Breadcrumbs = (props: BreadcrumbsProps) => {
  const { items, separator = DEFAULT_SEPARATOR } = props;
  const styles = useStyles();

  return (
    <div className={styles.wrapper}>
      <nav className={styles.nav}>
        <ol className={styles.list}>
          {items.map(({ label, url, icon }) => {
            return (
              <Fragment key={`${label}-${url}`}>
                <li className={cn(styles.item, styles.separator)} data-testid="breadcrumbs__separator">
                  {separator}
                </li>
                <li className={styles.item} data-testid="breadcrumbs__item" title={label}>
                  <LinkComponent to={url}>
                    <div className={styles.itemContent}>
                      {icon}
                      <span className={styles.itemLabel}>{label}</span>
                    </div>
                  </LinkComponent>
                </li>
              </Fragment>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
